/**
 * Диалог «Должности из структуры компании» шага «Должности» профрисков
 * (centrika-docs!26, `component.create-profrisk-order.positions`, раздел «Диалоговое окно»).
 * Множественный выбор должностей структуры: три кнопки массового выбора, группировка по
 * подразделениям, пометка «уже в заказе», счётчик «Выбрано: N», «Отмена» / «Добавить выбранные».
 *
 * Источник строк — существующий `GET /api/profrisk/dashboard` (design D5): он собран из структуры
 * компании (вакантные места включены) и несёт `riskLevel` = `not_assessed` («Не оценено») с учётом
 * исторических результатов и результатов завершённых заказов — это ось кнопки «Выбрать должности
 * без профрисков». Количество штатных единиц из структуры НЕ показывается и не подставляется.
 *
 * Пометка «уже в заказе» считается на фронте — сопоставлением строки структуры с должностями
 * текущего заказа по подразделению и должности (ровно так формулирует требование). Такая строка
 * неактивна, не выбирается ни одной кнопкой массового выбора и не входит в «Выбрано: N».
 *
 * «Добавить выбранные» уходит ОДНИМ атомарным запросом (`addPositionsBatch`, design D2): в заказ
 * попадают либо все отмеченные должности, либо ни одной.
 */
import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { profriskApi, type ProfriskDashboardRow, type ProfriskPosition } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  PROFRISK_STRUCTURE_ALREADY,
  PROFRISK_STRUCTURE_CANCEL,
  PROFRISK_STRUCTURE_DIALOG_HINT,
  PROFRISK_STRUCTURE_DIALOG_TITLE,
  PROFRISK_STRUCTURE_SELECT_ALL,
  PROFRISK_STRUCTURE_SELECT_NONE,
  PROFRISK_STRUCTURE_SELECT_NOT_ASSESSED,
  PROFRISK_STRUCTURE_SUBMIT,
} from './profrisk-copy'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'

/** Ключ сопоставления строки структуры с должностью заказа: подразделение + должность. */
function matchKey(department: string, position: string): string {
  return `${department.trim().toLowerCase()}\u0000${position.trim().toLowerCase()}`
}

interface StructureRow {
  /** Стабильный ключ строки — рабочее место структуры. */
  id: string
  department: string
  position: string
  positionId: string
  /** Профриски по должности не оценены — ось кнопки «Выбрать должности без профрисков». */
  notAssessed: boolean
  alreadyInOrder: boolean
}

export interface PositionsStructureModalProps {
  orderId: string
  /** Должности текущего заказа — для пометки «уже в заказе» (сопоставление на фронте). */
  positions: ProfriskPosition[]
  onClose: () => void
  /** Созданные должности в порядке входного массива (ответ батча). */
  onAdded: (added: ProfriskPosition[]) => void
}

export function PositionsStructureModal({
  orderId,
  positions,
  onClose,
  onAdded,
}: PositionsStructureModalProps) {
  const [rows, setRows] = useState<ProfriskDashboardRow[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    profriskApi
      .dashboard(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setRows(res.rows)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setLoadError(getErrorMessage(err, 'Не удалось загрузить структуру компании'))
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  const inOrder = useMemo(
    () => new Set(positions.map((p) => matchKey(p.departmentName, p.position))),
    [positions],
  )

  /** Строки структуры в порядке дашборда (он же — порядок добавления в заказ). */
  const structureRows = useMemo<StructureRow[]>(
    () =>
      rows.map((r) => ({
        id: r.workplaceId,
        department: r.department,
        position: r.position,
        positionId: r.positionId,
        notAssessed: r.riskLevel === 'not_assessed',
        alreadyInOrder: inOrder.has(matchKey(r.department, r.position)),
      })),
    [rows, inOrder],
  )

  const groups = useMemo(() => {
    const order: string[] = []
    const byDept = new Map<string, StructureRow[]>()
    for (const row of structureRows) {
      const key = row.department.trim() || 'Без подразделения'
      if (!byDept.has(key)) {
        byDept.set(key, [])
        order.push(key)
      }
      byDept.get(key)!.push(row)
    }
    return order.map((department) => ({ department, rows: byDept.get(department)! }))
  }, [structureRows])

  /**
   * Массовый выбор: каждая кнопка ЗАМЕНЯЕТ текущий набор своим (требование), должности с
   * пометкой «уже в заказе» не выбираются ни одной из кнопок.
   */
  function selectBy(predicate: (row: StructureRow) => boolean) {
    setSelected(new Set(structureRows.filter((r) => !r.alreadyInOrder && predicate(r)).map((r) => r.id)))
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function submit() {
    setSubmitError(null)
    setSubmitting(true)
    try {
      const picked = structureRows.filter((r) => selected.has(r.id) && !r.alreadyInOrder)
      const result = await profriskApi.addPositionsBatch(orderId, {
        positions: picked.map((r) => ({
          departmentName: r.department,
          position: r.position,
          positionId: r.positionId || undefined,
          source: 'structure',
        })),
      })
      onAdded(result.positions)
      onClose()
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Не удалось добавить должности'))
    } finally {
      setSubmitting(false)
    }
  }

  const selectedCount = selected.size

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent title={PROFRISK_STRUCTURE_DIALOG_TITLE} className="max-w-2xl">
        <Card
          header={PROFRISK_STRUCTURE_DIALOG_TITLE}
          footer={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-txt-secondary">Выбрано: {selectedCount}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose} disabled={submitting}>
                  {PROFRISK_STRUCTURE_CANCEL}
                </Button>
                <Button onClick={() => void submit()} disabled={submitting || selectedCount === 0}>
                  {PROFRISK_STRUCTURE_SUBMIT}
                </Button>
              </div>
            </div>
          }
        >
          <p className="mb-3 text-txt-secondary">{PROFRISK_STRUCTURE_DIALOG_HINT}</p>

          <div className="mb-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => selectBy(() => true)}>
              {PROFRISK_STRUCTURE_SELECT_ALL}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => selectBy((r) => r.notAssessed)}>
              {PROFRISK_STRUCTURE_SELECT_NOT_ASSESSED}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setSelected(new Set())}>
              {PROFRISK_STRUCTURE_SELECT_NONE}
            </Button>
          </div>

          {submitError ? (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          ) : null}
          {loadError ? (
            <Alert variant="danger" className="mb-3">
              {loadError}
            </Alert>
          ) : null}

          {loading ? (
            <p className="py-6 text-center text-txt-secondary">Загрузка…</p>
          ) : groups.length === 0 ? (
            <p className="py-6 text-center text-txt-secondary">
              В структуре компании пока нет должностей
            </p>
          ) : (
            <div className="max-h-96 overflow-y-auto pr-1">
              {groups.map((g) => (
                <div key={g.department} className="mb-4 last:mb-0">
                  <p className="mb-2 text-12 font-semibold uppercase tracking-wide text-txt-secondary">
                    {g.department}
                  </p>
                  <div className="flex flex-col divide-y divide-line">
                    {g.rows.map((row) => (
                      <label
                        key={row.id}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 py-2.5',
                          row.alreadyInOrder && 'cursor-not-allowed opacity-60',
                        )}
                      >
                        <Checkbox
                          checked={selected.has(row.id)}
                          disabled={row.alreadyInOrder}
                          onCheckedChange={() => toggle(row.id)}
                        />
                        <span className="min-w-0 flex-1 text-14 font-medium text-txt">
                          {row.position}
                        </span>
                        {row.alreadyInOrder ? (
                          <Badge variant="gray">{PROFRISK_STRUCTURE_ALREADY}</Badge>
                        ) : null}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </DialogContent>
    </Dialog>
  )
}
